import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AdminNavComponent } from '../../components/admin-nav/admin-nav.component';
import { DailyBar, DailyBarsComponent } from '../../components/shared/daily-bars.component';
import { PersonLinkComponent } from '../../components/shared/person-link.component';
import { UsersService } from '../../services/users-service.service';
import {
  AbuseArchiveDto,
  AbuseCaseDto,
  AbuseOverviewDto,
  AppealDto,
  BannedPersonaDto,
  MemberComplaintDto,
  ThresholdDto,
} from '../../data-transfer-objects/abuse-case.dto';

type Tab =
  | 'appeals'
  | 'cases'
  | 'complaints'
  | 'watchlist'
  | 'archives'
  | 'thresholds';

/** Thresholds as shown, grouped under the heading each one belongs to. */
interface ThresholdGroup {
  name: string;
  rows: ThresholdDto[];
}

/**
 * The abuse board.
 *
 * Two questions, in this order: is something happening right now, and who do I
 * act on. The health strip answers the first — organic traffic is one to three
 * new conversations a day, so a bar three times the height of its neighbours is
 * the whole signal, and it is visible before any account has been flagged. Both
 * previous incidents were noticed as traffic, not as a report.
 *
 * The queue answers the second. Accounts banned straight from the admin
 * endpoint appear alongside guard-flagged ones: a ban with no case attached
 * looks like it came from nowhere, and someone reading this a month later has
 * no way to tell which of the two happened.
 */
@Component({
  selector: 'app-abuse',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    MatButtonModule,
    MatProgressSpinnerModule,
    AdminNavComponent,
    DailyBarsComponent,
    PersonLinkComponent,
  ],
  templateUrl: './abuse.component.html',
  styleUrls: ['./abuse.component.scss'],
})
export class AbuseComponent implements OnInit {
  tab: Tab = 'cases';
  overview: AbuseOverviewDto | null = null;
  cases: AbuseCaseDto[] = [];
  complaints: MemberComplaintDto[] = [];
  personas: BannedPersonaDto[] = [];
  archives: AbuseArchiveDto[] = [];
  appeals: AppealDto[] = [];
  thresholds: ThresholdDto[] = [];
  thresholdGroups: ThresholdGroup[] = [];
  /** Edited values by key, holding only the rows actually touched. */
  edits: Record<string, number> = {};
  savingThresholds = false;
  loading = true;
  error: string | null = null;
  notice: string | null = null;
  /** Watchlist key awaiting its confirm click. */
  confirmingKey: string | null = null;
  busyKey: string | null = null;

  constructor(private usersService: UsersService) {}

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading = true;
    this.error = null;

    this.usersService.getAbuseOverview().subscribe({
      next: (o) => (this.overview = o),
      error: (err) => (this.error = `Could not load the overview: ${err?.message ?? err}`),
    });

    this.usersService.getAbuseCases().subscribe({
      next: (rows) => {
        this.cases = rows;
        this.loading = false;
      },
      error: (err) => {
        this.error = `Could not load cases: ${err?.message ?? err}`;
        this.loading = false;
      },
    });

    this.usersService.getAbuseComplaints().subscribe({
      next: (rows) => (this.complaints = rows),
      error: () => undefined,
    });

    this.usersService.getBannedPersonas().subscribe({
      next: (rows) => (this.personas = rows),
      error: () => undefined,
    });

    this.usersService.getAbuseArchives().subscribe({
      next: (rows) => (this.archives = rows),
      error: () => undefined,
    });

    this.usersService.getAppeals().subscribe({
      next: (rows) => {
        this.appeals = rows;
        // Somebody is waiting on an answer with a clock running, so the board
        // opens on that rather than on the case list.
        if (rows.some((a) => a.status === 'open')) this.tab = 'appeals';
      },
      error: () => undefined,
    });

    this.loadThresholds();
  }

  /**
   * Complaints nobody has looked at.
   *
   * The number that matters on this page: an account members objected to that
   * neither the guard nor an admin has touched. Everything else in the list is
   * a complaint with a decision already attached to it.
   */
  get unreviewedComplaints(): MemberComplaintDto[] {
    return this.complaints.filter((c) => !c.flagged && !c.banned);
  }

  /** Every block on record, across all accounts. Blocks carry no timestamp. */
  get blocksTotal(): number {
    return this.complaints.reduce((total, c) => total + c.blocks, 0);
  }

  /** What has been decided about a complaint, if anything. */
  complaintStatus(c: MemberComplaintDto): string {
    if (c.banned) return 'Banned';
    if (c.flagged) return 'Flagged';
    return 'Not reviewed';
  }

  get openAppeals(): AppealDto[] {
    return this.appeals.filter((a) => a.status === 'open');
  }

  /**
   * How an appeal's deadline should read.
   *
   * The two-working-day promise is made on the on-hold screen and in the mail;
   * showing it here is what makes it something other than an aspiration.
   */
  dueLabel(appeal: AppealDto): string {
    if (!appeal.dueAt) return '—';
    const due = new Date(appeal.dueAt).getTime();
    const diff = due - Date.now();
    const day = 24 * 60 * 60 * 1000;
    if (diff < 0) return `Overdue ${Math.max(1, Math.ceil(-diff / day))}d`;
    if (diff < day) return 'Due today';
    if (diff < 2 * day) return 'Due tomorrow';
    return `Due ${new Date(appeal.dueAt).toLocaleDateString(undefined, { weekday: 'short' })}`;
  }

  isOverdue(appeal: AppealDto): boolean {
    return (
      appeal.status === 'open' &&
      !!appeal.dueAt &&
      new Date(appeal.dueAt).getTime() < Date.now()
    );
  }

  loadThresholds(): void {
    this.usersService.getGuardThresholds().subscribe({
      next: (rows) => this.applyThresholds(rows),
      error: (err) =>
        (this.error = `Could not load the thresholds: ${err?.message ?? err}`),
    });
  }

  private applyThresholds(rows: ThresholdDto[]): void {
    this.thresholds = rows;
    this.edits = {};
    // Groups in the order the backend lists them, so the reading order stays
    // established → new → phrasing → watchlist rather than alphabetical.
    const groups: ThresholdGroup[] = [];
    rows.forEach((row) => {
      const existing = groups.find((g) => g.name === row.group);
      if (existing) {
        existing.rows.push(row);
        return;
      }
      groups.push({ name: row.group, rows: [row] });
    });
    this.thresholdGroups = groups;
  }

  /** The value a row should display: the edit if there is one, else the saved one. */
  shownValue(row: ThresholdDto): number {
    return this.edits[row.key] ?? row.value;
  }

  onThresholdInput(row: ThresholdDto, event: Event): void {
    const raw = (event.target as HTMLInputElement).value;
    const parsed = Number(raw);
    if (raw === '' || Number.isNaN(parsed)) {
      delete this.edits[row.key];
      return;
    }
    if (parsed === row.value) {
      delete this.edits[row.key];
      return;
    }
    this.edits[row.key] = parsed;
  }

  resetToDefault(row: ThresholdDto): void {
    if (row.defaultValue === row.value) {
      delete this.edits[row.key];
      return;
    }
    this.edits[row.key] = row.defaultValue;
  }

  get editedKeys(): string[] {
    return Object.keys(this.edits);
  }

  /** Whether a row is outside the range the backend will accept. */
  outOfRange(row: ThresholdDto): boolean {
    const value = this.shownValue(row);
    return value < row.min || value > row.max || !Number.isInteger(value);
  }

  get canSaveThresholds(): boolean {
    return (
      this.editedKeys.length > 0 &&
      !this.savingThresholds &&
      !this.thresholds.some((row) => this.outOfRange(row))
    );
  }

  saveThresholds(): void {
    if (!this.canSaveThresholds) return;
    this.savingThresholds = true;
    this.error = null;
    this.notice = null;
    const changedCount = this.editedKeys.length;

    this.usersService.updateGuardThresholds(this.edits).subscribe({
      next: (rows) => {
        this.applyThresholds(rows);
        this.savingThresholds = false;
        this.notice =
          `Saved ${changedCount} threshold${changedCount === 1 ? '' : 's'}. ` +
          'Live within a minute — the guard caches them briefly.';
      },
      error: (err) => {
        this.savingThresholds = false;
        this.error =
          err?.error?.error ??
          `Could not save the thresholds: ${err?.message ?? err}`;
      },
    });
  }

  discardThresholdEdits(): void {
    this.edits = {};
  }

  /** Status a row should be read as, worst first. */
  status(c: AbuseCaseDto): 'banned' | 'flagged' | 'clear' {
    if (c.banned) return 'banned';
    if (c.alertLevel !== 'none') return 'flagged';
    return 'clear';
  }

  statusLabel(c: AbuseCaseDto): string {
    if (c.banned) return c.autoDisabled ? 'Auto-banned' : 'Banned';
    if (c.alertLevel === 'flagged') return 'Needs review';
    return 'Clear';
  }

  /**
   * Bar height as a percentage of the busiest day in the window.
   *
   * Relative, not absolute: the interesting shape is one day standing well
   * above the rest, and at a baseline of one to three chats a day an absolute
   * scale would render every normal day as nothing.
   */
  get dailyBars(): DailyBar[] {
    return (this.overview?.daily ?? []).map((d) => ({
      day: d.day,
      value: d.newChats,
      title: `${d.day}: ${d.newChats} new chats, ${d.messages} messages`,
    }));
  }

  removePersona(key: string): void {
    if (this.confirmingKey !== key) {
      this.confirmingKey = key;
      return;
    }
    this.busyKey = key;
    this.usersService.removeBannedPersona(key).subscribe({
      next: () => {
        this.personas = this.personas.filter((p) => p.key !== key);
        this.notice = `Removed "${key}" from the watchlist.`;
        this.busyKey = null;
        this.confirmingKey = null;
      },
      error: (err) => {
        this.error = `Could not remove the persona: ${err?.message ?? err}`;
        this.busyKey = null;
        this.confirmingKey = null;
      },
    });
  }
}
